package com.flowyride.feature.planner.ui.components

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.flowyride.core.domain.favorite.FavoriteSource
import com.flowyride.core.domain.favorite.FavoriteSummary
import com.flowyride.feature.planner.FavoritesUiState
import com.flowyride.feature.planner.resources.Res
import com.flowyride.feature.planner.resources.cancel
import com.flowyride.feature.planner.resources.favorite_delete
import com.flowyride.feature.planner.resources.favorite_delete_confirm
import com.flowyride.feature.planner.resources.favorite_imported_badge
import com.flowyride.feature.planner.resources.favorite_options
import com.flowyride.feature.planner.resources.favorite_rename
import com.flowyride.feature.planner.resources.favorites_empty
import com.flowyride.feature.planner.resources.favorites_failed
import com.flowyride.feature.planner.resources.favorites_import_gpx
import com.flowyride.feature.planner.resources.favorites_title
import com.flowyride.feature.planner.resources.retry
import com.flowyride.feature.planner.resources.route_summary
import com.flowyride.feature.planner.ui.formatDistance
import com.flowyride.feature.planner.ui.formatDuration
import org.jetbrains.compose.resources.stringResource

/** The rider's saved rides, and the way in for GPX files. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun FavoritesSheet(
    state: FavoritesUiState,
    onOpen: (FavoriteSummary) -> Unit,
    onRename: (FavoriteSummary) -> Unit,
    onDelete: (FavoriteSummary) -> Unit,
    onImportGpx: () -> Unit,
    onRetry: () -> Unit,
    onDismiss: () -> Unit,
) {
    var deleting by remember { mutableStateOf<FavoriteSummary?>(null) }
    ModalBottomSheet(onDismissRequest = onDismiss, sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)) {
        Column(Modifier.padding(horizontal = 16.dp).padding(bottom = 24.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.favorites_title), style = MaterialTheme.typography.titleLarge, modifier = Modifier.weight(1f))
                if (state.isBusy) CircularProgressIndicator(Modifier.size(20.dp), strokeWidth = 2.dp)
            }
            OutlinedButton(onClick = onImportGpx, modifier = Modifier.fillMaxWidth()) {
                Text(stringResource(Res.string.favorites_import_gpx))
            }
            HorizontalDivider()
            when {
                state.isLoading && state.items.isEmpty() -> Box(Modifier.fillMaxWidth().padding(24.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
                state.failed -> Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(
                        stringResource(Res.string.favorites_failed),
                        color = MaterialTheme.colorScheme.error,
                        modifier = Modifier.weight(1f),
                    )
                    TextButton(onClick = onRetry) { Text(stringResource(Res.string.retry)) }
                }
                state.items.isEmpty() -> Text(
                    stringResource(Res.string.favorites_empty),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                else -> LazyColumn(Modifier.heightIn(max = 480.dp)) {
                    items(state.items, key = { it.id }) { favorite ->
                        FavoriteRow(
                            favorite = favorite,
                            enabled = !state.isBusy,
                            onOpen = { onOpen(favorite) },
                            onRename = { onRename(favorite) },
                            onDelete = { deleting = favorite },
                        )
                    }
                }
            }
        }
    }
    deleting?.let { favorite ->
        AlertDialog(
            onDismissRequest = { deleting = null },
            title = { Text(stringResource(Res.string.favorite_delete_confirm, favorite.name)) },
            confirmButton = {
                TextButton(onClick = {
                    deleting = null
                    onDelete(favorite)
                }) { Text(stringResource(Res.string.favorite_delete), color = MaterialTheme.colorScheme.error) }
            },
            dismissButton = { TextButton(onClick = { deleting = null }) { Text(stringResource(Res.string.cancel)) } },
        )
    }
}

@Composable
private fun FavoriteRow(
    favorite: FavoriteSummary,
    enabled: Boolean,
    onOpen: () -> Unit,
    onRename: () -> Unit,
    onDelete: () -> Unit,
) {
    var menuOpen by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth().clickable(enabled = enabled, onClick = onOpen).padding(vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(favorite.name, style = MaterialTheme.typography.bodyLarge, maxLines = 1, overflow = TextOverflow.Ellipsis)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                Text(
                    stringResource(Res.string.route_summary, formatDistance(favorite.distanceMeters), formatDuration(favorite.durationSeconds)),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                if (favorite.source == FavoriteSource.IMPORTED) ImportedBadge()
            }
        }
        val description = stringResource(Res.string.favorite_options)
        Box {
            IconButton(onClick = { menuOpen = true }, modifier = Modifier.semantics { contentDescription = description }) {
                Text("⋮", style = MaterialTheme.typography.titleLarge)
            }
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                DropdownMenuItem(text = { Text(stringResource(Res.string.favorite_rename)) }, onClick = {
                    menuOpen = false
                    onRename()
                })
                DropdownMenuItem(text = { Text(stringResource(Res.string.favorite_delete)) }, onClick = {
                    menuOpen = false
                    onDelete()
                })
            }
        }
    }
}

/** Marks rides that come from a GPX file rather than from FlowyRide. */
@Composable
internal fun ImportedBadge(modifier: Modifier = Modifier) {
    Surface(modifier, shape = MaterialTheme.shapes.small, color = MaterialTheme.colorScheme.secondaryContainer) {
        Text(
            stringResource(Res.string.favorite_imported_badge),
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSecondaryContainer,
            modifier = Modifier.padding(horizontal = 6.dp, vertical = 2.dp),
        )
    }
}
